// ExportsPanel — scope-aware export catalog.
//
// Renders the available exports for a single property OR a whole project.
// Synchronous exports (CSV/XLSX/DOCX) run immediately in the browser.
// Async bundles (zips) now go through the in-browser DownloadManager — the
// download lives in a floating tray that survives route changes and warns
// on tab close. No more edge functions for bundles.

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Download, FileSpreadsheet, FileText, Image as ImageIcon, Package, Loader2 } from "lucide-react";
import { toast } from "@/shared/hooks/use-toast";
import {
  exportSurvey,
  exportAssets,
  exportDeficiencies,
  exportTemplate,
  type ExportScope,
} from "@/features/exports/services";
import {
  buildPropertyPhotosBundle,
  buildSiteBundle,
  buildProjectBundle,
  buildDeficienciesPortfolioBundle,
} from "@/features/exports/services/bundles";
import { useDownloadManager } from "@/features/exports/services/DownloadManagerContext";
import { sanitize } from "@/features/exports/services/_helpers";

interface ExportsPanelProps {
  scope: ExportScope;
  // Label used in filenames. Property name or project name.
  label: string;
  className?: string;
}

type SyncFormat = "xlsx" | "csv" | "docx";

interface SyncExportSpec {
  key: string;
  title: string;
  description: string;
  icon: typeof FileSpreadsheet;
  formats: SyncFormat[];
  run: (format: SyncFormat) => Promise<void>;
  bundle?: {
    label: string;
    onClick: () => void;
  };
}

interface AsyncExportSpec {
  key: string;
  title: string;
  description: string;
  icon: typeof Package;
  enqueue: () => void;
}

export function ExportsPanel({ scope, label, className }: ExportsPanelProps) {
  const { enqueue } = useDownloadManager();
  const syncExports = buildSyncSpecs(scope, label, enqueue);
  const asyncExports = buildAsyncSpecs(scope, label, enqueue);

  const [busyKey, setBusyKey] = useState<string | null>(null);

  const handleSync = async (spec: SyncExportSpec, format: SyncFormat) => {
    const key = `${spec.key}-${format}`;
    setBusyKey(key);
    try {
      await spec.run(format);
      toast({ title: `${spec.title} exported`, description: `${format.toUpperCase()} downloaded.` });
    } catch (err) {
      console.error(err);
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className={className}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {syncExports.map((spec) => {
          const Icon = spec.icon;
          return (
            <Card key={spec.key}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="h-4 w-4" />
                  {spec.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">{spec.description}</p>
                <div className="flex gap-2">
                  {spec.formats.map((fmt) => {
                    const k = `${spec.key}-${fmt}`;
                    const busy = busyKey === k;
                    return (
                      <Button
                        key={fmt}
                        size="sm"
                      variant="outline"
                        onClick={() => handleSync(spec, fmt)}
                        disabled={busy}
                        className="gap-2"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        {fmt.toUpperCase()}
                      </Button>
                    );
                  })}
                  {spec.bundle && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={spec.bundle.onClick}
                      className="gap-2"
                    >
                      <Package className="h-4 w-4" />
                      {spec.bundle.label}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {asyncExports.map((spec) => {
          const Icon = spec.icon;
          return (
            <Card key={spec.key}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="h-4 w-4" />
                  {spec.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">{spec.description}</p>
                <Button size="sm" variant="outline" onClick={spec.enqueue} className="gap-2">
                  <Package className="h-4 w-4" />
                  Build bundle
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function buildSyncSpecs(
  scope: ExportScope,
  label: string,
  enqueue: ReturnType<typeof useDownloadManager>["enqueue"],
): SyncExportSpec[] {
  const isProperty = scope.kind === "property";

  const specs: SyncExportSpec[] = [
    {
      key: "survey",
      title: isProperty ? "Survey data" : "Cross-store survey",
      description: isProperty
        ? "All Section 4 question answers for this site."
        : "All sites × all survey questions in a single dataset.",
      icon: FileText,
      formats: ["xlsx", "csv"],
      run: (format) =>
        exportSurvey({
          projectId: scope.projectId,
          propertyId: isProperty ? scope.propertyId : undefined,
          projectName: label,
          format: format as "xlsx" | "csv",
        }),
    },
    {
      key: "assets",
      title: isProperty ? "Asset register" : "Cross-store assets",
      description: isProperty
        ? "All assets for this site with nameplate data and condition."
        : "All assets across all sites in a single dataset.",
      icon: FileSpreadsheet,
      formats: ["xlsx", "csv"],
      run: (format) =>
        exportAssets({
          projectId: scope.projectId,
          propertyId: isProperty ? scope.propertyId : undefined,
          label,
          format: format as "xlsx" | "csv",
        }),
    },
    {
      key: "deficiencies",
      title: isProperty ? "Deficiencies" : "Portfolio deficiencies",
      description: isProperty
        ? "All deficiencies and recommendations for this site."
        : "All deficiencies and recommendations across the portfolio.",
      icon: FileSpreadsheet,
      formats: ["xlsx", "csv"],
      run: (format) =>
        exportDeficiencies({
          projectId: scope.projectId,
          propertyId: isProperty ? scope.propertyId : undefined,
          label,
          format: format as "xlsx" | "csv",
        }),
      bundle: {
        label: "ZIP",
        onClick: () => {
          const bundleSlug = sanitize(label);
          enqueue({
            label: `Deficiencies package — ${label}`,
            filename: `${bundleSlug}_deficiencies_package.zip`,
            build: (ctx) => buildDeficienciesPortfolioBundle(
              {
                projectId: scope.projectId,
                propertyId: isProperty ? scope.propertyId : undefined,
              },
              ctx,
            ),
          });
        },
      },
    },
  ];

  if (!isProperty) {
    specs.push({
      key: "template",
      title: "Survey template",
      description: "Blank survey questionnaire as a Word document.",
      icon: FileText,
      formats: ["docx"],
      run: () => exportTemplate({ projectId: scope.projectId, projectName: label }),
    });
  }

  return specs;
}

function buildAsyncSpecs(
  scope: ExportScope,
  label: string,
  enqueue: ReturnType<typeof useDownloadManager>["enqueue"],
): AsyncExportSpec[] {
  const slug = sanitize(label);

  if (scope.kind === "property") {
    return [
      {
        key: "photos",
        title: "Photo bundle",
        description: "All nameplate and deficiency photos zipped by asset/category.",
        icon: ImageIcon,
        enqueue: () => {
          enqueue({
            label: `Photo bundle — ${label}`,
            filename: `${slug}_photos.zip`,
            build: (ctx) => buildPropertyPhotosBundle(
              { projectId: scope.projectId, propertyId: scope.propertyId },
              ctx,
            ),
          });
        },
      },
      {
        key: "site-bundle",
        title: "Download everything",
        description: "Digital twin files + photos + survey + asset data in one zip.",
        icon: Package,
        enqueue: () => {
          enqueue({
            label: `Site bundle — ${label}`,
            filename: `${slug}_site_bundle.zip`,
            build: (ctx) => buildSiteBundle(
              { projectId: scope.projectId, propertyId: scope.propertyId },
              ctx,
            ),
          });
        },
      },
    ];
  }

  return [
    {
      key: "project-bundle",
      title: "Project bundle",
      description: "Per-property CSV roll-ups + clickable HTML index. Photos and twin files are linked, not embedded.",
      icon: Package,
      enqueue: () => {
        enqueue({
          label: `Project bundle — ${label}`,
          filename: `${slug}_project_bundle.zip`,
          build: (ctx) => buildProjectBundle({ projectId: scope.projectId }, ctx),
        });
      },
    },
  ];
}
